import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, Instagram, Facebook, MessageCircle } from "lucide-react";
import { BUSINESS } from "../config";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/hybrid-flooring", label: "Hybrid Flooring" },
  { to: "/accessories", label: "Scotia & Trims" },
  { to: "/services", label: "Our Services" },
  { to: "/colours", label: "Flooring Colours" },
  { to: "/projects", label: "Recent Projects" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-[#C87A38] rounded-sm ${
    isActive ? "text-[#C87A38]" : "text-[#1C1C1C] hover:text-[#C87A38]"
  }`;

export const Layout = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#1C1C1C]">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-black/5" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo" aria-label="Zum Flooring home">
            <img src="/assets/logo.png" alt="Zum Flooring logo" className="h-10 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 whitespace-nowrap" data-testid="nav-desktop" aria-label="Main navigation">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneTel}
              className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#4A3018] hover:text-[#C87A38] transition-colors whitespace-nowrap"
              data-testid="header-call-link"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
            </a>
            <Link
              to="/get-a-free-quote"
              className="hidden sm:inline-flex items-center whitespace-nowrap rounded-full bg-[#C87A38] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#A6632B] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:ring-offset-2"
              data-testid="header-quote-button"
            >
              Get a Free Quote
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-black/5 transition-colors"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-black/5 bg-white px-6 py-4 flex flex-col gap-1" data-testid="nav-mobile" aria-label="Mobile navigation">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-2.5 text-base font-medium transition-colors ${isActive ? "text-[#C87A38]" : "text-[#1C1C1C]"}`
                }
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/get-a-free-quote"
              className="mt-2 inline-flex justify-center rounded-full bg-[#C87A38] px-5 py-3 text-sm font-semibold text-white"
              data-testid="mobile-nav-quote-button"
            >
              Get a Free Quote
            </Link>
          </nav>
        )}
      </header>

      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      <footer className="bg-[#4A3018] text-white/80" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src="/assets/logo.png" alt="Zum Flooring logo" className="h-14 w-auto bg-white rounded-md p-1.5 mb-4" />
            <p className="text-sm leading-relaxed">{BUSINESS.slogan}</p>
            <p className="text-sm mt-3 leading-relaxed">
              Quality hybrid flooring supply and installation for homes across {BUSINESS.serviceArea}.
            </p>
          </div>
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="hover:text-white transition-colors" data-testid="footer-link-supply-install">Supply and Installation</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors" data-testid="footer-link-supply-only">Flooring Supply Only</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors" data-testid="footer-link-installation-only">Installation Only</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors" data-testid="footer-link-subfloor">Subfloor Preparation</Link></li>
              <li><Link to="/accessories" className="hover:text-white transition-colors" data-testid="footer-link-scotia">Scotia Installation</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors" data-testid="footer-link-removal">Flooring Removal and Repairs</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white transition-colors" data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/get-a-free-quote" className="hover:text-white transition-colors" data-testid="footer-nav-quote">
                  Get a Free Quote
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 shrink-0" />
                <a href={BUSINESS.phoneTel} className="hover:text-white transition-colors" data-testid="footer-phone">{BUSINESS.phoneDisplay}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 shrink-0" />
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-white transition-colors" data-testid="footer-email">{BUSINESS.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <span data-testid="footer-service-area">{BUSINESS.serviceArea}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Mon–Fri 8am–5pm, Sat 8am–2pm</span>
              </li>
            </ul>
            <div className="flex items-center gap-4 mt-5">
              <a href={BUSINESS.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={BUSINESS.facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white transition-colors" data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:text-white transition-colors" data-testid="footer-whatsapp">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-white/60 flex flex-col sm:flex-row justify-between gap-2">
            <span data-testid="footer-copyright">© {new Date().getFullYear()} Zum Flooring. All rights reserved.</span>
            <span className="flex items-center gap-4">
              <span>Hybrid flooring supply and installation, Melbourne VIC.</span>
              <Link to="/privacy" className="hover:text-white transition-colors underline underline-offset-2" data-testid="footer-privacy-link">Privacy Policy</Link>
            </span>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/90 backdrop-blur-xl border-t border-black/10 p-3" data-testid="mobile-sticky-quote-bar">
        <Link
          to="/get-a-free-quote"
          className="flex items-center justify-center rounded-full bg-[#C87A38] px-5 py-3.5 text-base font-semibold text-white shadow-lg"
          data-testid="mobile-sticky-quote-button"
        >
          Get a Free Quote
        </Link>
      </div>
    </div>
  );
};
